customElements.whenDefined('side-drawer').then(() => {
  class NavDrawer extends SideDrawer {
    constructor() {
      super();
      this.tabs = this.querySelectorAll('.nav-drawer__tab');
      this.panels = this.querySelectorAll('.nav-drawer__panel');
      this.footers = this.querySelectorAll('.nav-drawer__footer');
      this.bindEvents();
    }

    connectedCallback() {
      if (this.parentElement && this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
    }

    handleClick(evt) {
      if (evt.target.closest('.js-close-drawer') || evt.target === this.overlay) {
        this.close();
      }
    }

    bindEvents() {
      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => this.switchTab(tab.dataset.tab));
      });

      this.querySelectorAll('.nav-drawer__item--parent').forEach(btn => {
        btn.addEventListener('click', () => this.openSublevel(btn.dataset.sublevel));
      });

      this.querySelectorAll('.nav-drawer__back').forEach(btn => {
        btn.addEventListener('click', () => this.closeSublevel(btn));
      });

      // Delegated on document (bound once) so the triggers open the drawer reliably,
      // regardless of when the buttons are rendered/moved and independent of per-element
      // binding timing.
      //
      // On touch devices (e.g. Surface Pro), a tap on the horizontal nav sometimes does NOT
      // produce a usable `click` (gesture/hover emulation, or another nav handler interfering),
      // so we ALSO listen for Pointer Events (`pointerup`), which fire reliably for touch/pen.
      // A short timestamp guard prevents the drawer from being opened twice when both a
      // pointerup and its compatibility click fire for the same tap.
      if (!NavDrawer.openTriggerBound) {
        NavDrawer.openTriggerBound = true;
        NavDrawer.lastTriggerTs = 0;
        NavDrawer.lastTrigger = null;

        const openFromTrigger = (trigger) => {
          const now = Date.now();
          // Dedupe the pointerup + compatibility-click pair for the same tap only.
          if (trigger === NavDrawer.lastTrigger && now - NavDrawer.lastTriggerTs < 600) return;
          NavDrawer.lastTrigger = trigger;
          NavDrawer.lastTriggerTs = now;

          const drawer = document.getElementById('nav-drawer');
          if (!drawer || typeof drawer.open !== 'function') return;

          const tab = trigger.dataset.drawerTab;
          if (tab && typeof drawer.switchTab === 'function') drawer.switchTab(tab);
          drawer.open(trigger);
        };

        document.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-open-nav-drawer]');
          if (!trigger) return;
          e.preventDefault();
          openFromTrigger(trigger);
        });

        // Touch / pen: handle via pointerup (mouse is already handled by click above).
        document.addEventListener('pointerup', (e) => {
          if (e.pointerType === 'mouse') return;
          const trigger = e.target.closest('[data-open-nav-drawer]');
          if (!trigger) return;
          e.preventDefault();
          openFromTrigger(trigger);
        });
      }

      const mobileMenu = document.querySelector('main-menu[data-drawer-mobile]');
      if (mobileMenu) {
        const burgerBtn = mobileMenu.querySelector('.main-menu__toggle');
        if (burgerBtn) {
          burgerBtn.addEventListener('click', (e) => {
            if (!window.matchMedia('(min-width: 769px)').matches) {
              e.preventDefault();
              e.stopImmediatePropagation();
              const disclosure = mobileMenu.querySelector('.main-menu__disclosure');
              if (disclosure && disclosure.open) {
                disclosure.open = false;
                disclosure.classList.remove('is-open');
              }
              this.open(burgerBtn);
            }
          }, true);
        }
      }

      if (Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          if (e.target === this || this.contains(e.target)) {
            this.open();
          }
        });
        document.addEventListener('shopify:block:deselect', (e) => {
          if (e.target === this || this.contains(e.target)) {
            this.close();
          }
        });
      }
    }

    switchTab(tabName) {
      this.tabs.forEach(t => {
        const isActive = t.dataset.tab === tabName;
        t.classList.toggle('is-active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.panels.forEach(p => {
        p.classList.toggle('is-active', p.dataset.panel === tabName);
      });

      this.footers.forEach(f => {
        f.classList.toggle('is-active', f.dataset.panelFooter === tabName);
      });

      this.resetSublevels();
    }

    openSublevel(id) {
      const panel = this.querySelector(`[data-sublevel-panel="${id}"]`);
      if (!panel) return;

      const activePanel = panel.closest('.nav-drawer__panel');
      if (!activePanel) return;

      activePanel.querySelector('.nav-drawer__level--root').classList.remove('is-active');

      activePanel.querySelectorAll('.nav-drawer__sublevel').forEach(sl => {
        sl.classList.remove('is-active');
      });

      panel.classList.add('is-active');

      this.footers.forEach(f => f.classList.remove('is-active'));

      this.querySelector('.nav-drawer__content').scrollTop = 0;
    }

    closeSublevel(backBtn) {
      const sublevel = backBtn.closest('.nav-drawer__sublevel');
      if (!sublevel) return;

      const activePanel = sublevel.closest('.nav-drawer__panel');
      if (!activePanel) return;

      sublevel.classList.remove('is-active');
      activePanel.querySelector('.nav-drawer__level--root').classList.add('is-active');

      const tabName = activePanel.dataset.panel;
      this.footers.forEach(f => {
        f.classList.toggle('is-active', f.dataset.panelFooter === tabName);
      });

      this.querySelector('.nav-drawer__content').scrollTop = 0;
    }

    resetSublevels() {
      this.querySelectorAll('.nav-drawer__sublevel.is-active').forEach(sl => {
        sl.classList.remove('is-active');
      });
      this.querySelectorAll('.nav-drawer__level--root').forEach(root => {
        root.classList.add('is-active');
      });
    }

    close(callback) {
      super.close(() => {
        this.resetSublevels();
        if (callback) callback();
      });
    }
  }

  customElements.define('nav-drawer', NavDrawer);
});
